/** Frontend wrapper for /bot-task-schedules: recurring jobs for a workspace
 * bot. A schedule runs as the person who set it up. The server builds the
 * plain-English `description`; show it rather than rebuilding it. */
import { API_BASE, getAuthHeaders } from '../utils/supabase/client';
import { throwApiError } from './BotApiError';
import type { BotTask, BotTaskDeliverable } from './BotTasksService';

export type ScheduleFrequency = 'daily' | 'weekdays' | 'weekly' | 'monthly';
export type ScheduleDelivery = 'chat' | 'notification';
export type SchedulePausedReason = 'owner' | 'admin' | 'owner_no_access' | 'bot_disabled' | 'failures';

export interface BotSchedule {
  id: string;
  botId: string;
  botName: string | null;
  workspaceId: string;
  ownerId: string;
  ownerName: string | null;
  isOwner: boolean;
  /** Null when an admin views someone else's schedule. */
  prompt: string | null;
  frequency: ScheduleFrequency;
  /** 1 = Monday .. 7 = Sunday. */
  weekdays: number[];
  monthDay: number | null;
  time: string;
  timezone: string;
  description: string;
  deliverTo: ScheduleDelivery;
  /** Absent on older servers. */
  deliverable?: string | null;
  status: 'active' | 'paused';
  pausedReason: SchedulePausedReason | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastTaskId: string | null;
  consecutiveFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface BotScheduleInput {
  prompt: string;
  frequency: ScheduleFrequency;
  weekdays?: number[];
  monthDay?: number;
  time: string;
  deliverTo: ScheduleDelivery;
  /** Omit for a plain schedule. On update, `null` clears it. */
  deliverable?: BotTaskDeliverable | null;
}

export interface BotScheduleList { enabled: boolean; schedules: BotSchedule[] }

const BASE = `${API_BASE}/bot-task-schedules`;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { ...init, headers: await getAuthHeaders({ 'Content-Type': 'application/json' }) });
  if (!res.ok) return throwApiError(res);
  return res.json() as Promise<T>;
}

const id = (value: string) => encodeURIComponent(value);

export const botTaskSchedulesService = {
  async list(params: { workspaceId: string; botId?: string; scope: 'mine' | 'workspace' }): Promise<BotScheduleList> {
    const query = new URLSearchParams({ workspaceId: params.workspaceId, scope: params.scope, ...(params.botId ? { botId: params.botId } : {}) });
    const data = await request<Partial<BotScheduleList> | null>(`?${query}`);
    return { enabled: data?.enabled === true, schedules: Array.isArray(data?.schedules) ? data.schedules : [] };
  },
  /** The browser's timezone goes with a new schedule so "08:00" means the
   * person's own 08:00. */
  async create(input: BotScheduleInput & { botId: string; workspaceId: string }): Promise<BotSchedule> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (await request<{ schedule: BotSchedule }>('', { method: 'POST', body: JSON.stringify({ ...input, ...(timezone ? { timezone } : {}) }) })).schedule;
  },
  async update(scheduleId: string, patch: Partial<BotScheduleInput> & { status?: 'active' | 'paused' }): Promise<BotSchedule> {
    return (await request<{ schedule: BotSchedule }>(`/${id(scheduleId)}`, { method: 'PATCH', body: JSON.stringify(patch) })).schedule;
  },
  async remove(scheduleId: string): Promise<void> {
    await request<{ ok: true }>(`/${id(scheduleId)}`, { method: 'DELETE' });
  },
  /** `requestId` names one press of Run now, so a retry after a lost reply
   * does not start a second task. */
  async runNow(scheduleId: string, requestId: string): Promise<BotTask> {
    return (await request<{ task: BotTask }>(`/${id(scheduleId)}/run`, { method: 'POST', body: JSON.stringify({ requestId }) })).task;
  },
};
